import {
    ChangeEvent,
    ClipboardEventHandler,
    FormEventHandler,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    MouseEvent as ReactMouseEvent,
} from "react"
import cn from "classnames"

import { Headline3, Modal, Text, Button } from "ui-kit/components-branded/index"
import { Spinner } from "components/Spinner"
import { useCountdown } from "utils/hooks"
import { DiscountSendCodeResponse } from "utils/interfaces"
import style from "modules/discount/VerificationCodeModal/style.module.scss"
import { format } from "utils/datetime-format-utils"
import { IS_PRODUCTION } from "utils/env-management"
import { httpClient } from "utils/infrastructure/httpClient"

const CODE_LENGTH = 5
const PLACEHOLDER = "—"

function getEmpty() {
    return Array(CODE_LENGTH).fill(PLACEHOLDER).join("")
}

function isNumbersOnly(value: string) {
    return value.match(/\D/) === null
}

type VerificationCodeModalStatus = "open" | "sending" | "sent" | "verifying" | "verified" | "error"

interface Props {
    email: string
    enable: boolean
    onVerified: () => Promise<void>
    onCancel?: () => void
}

export const VerificationCodeModal = ({ email, enable = false, onVerified, onCancel }: Props) => {
    const [code, setCode] = useState(getEmpty())
    const [verificationCode, setVerificationCode] = useState<string>()
    const [verificationToken, setVerificationToken] = useState<string>()

    const changeCode = useCallback(
        (code: string) => {
            setCode(
                code +
                    Array(CODE_LENGTH - code.length)
                        .fill(PLACEHOLDER)
                        .join("")
            )
        },
        [code]
    )
    const [status, setStatus] = useState<VerificationCodeModalStatus>("open")
    const [error, setError] = useState("")
    const [resendTimeout, setResendTimeout] = useState(new Date())
    const countdown = useCountdown(resendTimeout)
    const inputRef = useRef<HTMLInputElement>(null)

    const sendVerification = async (code: string) => {
        try {
            return await httpClient.post<unknown>("client/auth2/verification-code/verify/email", {
                verification_code: code,
                verification_token: verificationToken,
                email: email,
            })
        } catch {
            setError("Неверный код")
            setStatus("error")
        }
    }

    const send = async () => {
        setStatus("sending")

        try {
            const data = await httpClient.post<DiscountSendCodeResponse>(
                "client/auth2/verification-code/send",
                {
                    email: email,
                }
            )

            if (!data) return
            setVerificationToken(data.verification_token)
            setResendTimeout(new Date(new Date().getTime() + data.resend_timeout * 1000))
            setStatus("sent")
            inputRef.current?.focus()
            if (!IS_PRODUCTION) {
                setVerificationCode(data.verification_code)
            }
        } catch (e) {
            setError("Ошибка отправки кода")
            setStatus("error")
        }
    }

    const verify = async (code: string) => {
        setStatus("verifying")
        const response = await sendVerification(code)
        if (!response) return
        setStatus("verified")
        void onVerified?.()
    }

    const change = (event: ChangeEvent<HTMLInputElement>) => {
        const s = event.target.value
        const code = s.replaceAll(PLACEHOLDER, "")
        if (!isNumbersOnly(code) && code.length > 0) return
        changeCode(code)
    }

    const paste: ClipboardEventHandler<HTMLInputElement> = (event) => {
        event.preventDefault()
        const s = event.clipboardData.getData("text")
        const code = s.replaceAll(/\D+/g, "")
        changeCode(code.substring(0, CODE_LENGTH))
    }

    const inputHandler: FormEventHandler<HTMLInputElement> = (event) => {
        // @ts-expect-error, cause: TS2339: Property 'data' does not exist on type 'Event'.
        const data = event.nativeEvent.data as string | null
        if (data === null) return
        if (!isNumbersOnly(data)) event.preventDefault()
    }

    const cancel = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        onCancel?.()
    }

    useLayoutEffect(() => {
        const focus = () => {
            inputRef.current?.focus()
        }
        inputRef.current?.focus()
        inputRef.current?.setSelectionRange(0, 0)
        inputRef.current?.addEventListener("blur", focus)
        return () => {
            inputRef.current?.removeEventListener("blur", focus)
        }
    }, [])

    useEffect(() => {
        setStatus("open")
        setError("")
        const codeValue = code.replaceAll(PLACEHOLDER, "")
        const position = codeValue.length
        inputRef.current?.setSelectionRange(position, position)
        if (codeValue.length >= CODE_LENGTH) {
            void verify(codeValue)
        }
    }, [code])

    useEffect(() => {
        if (enable) {
            void send()
        }
    }, [enable])

    return (
        <Modal isActive={enable}>
            <Modal.Content
                className={style.wrapper}
                data-testid="discount-verification-modal"
                isFullHeightMobile
                padding="S"
                width="M"
            >
                {verificationCode && (
                    <div
                        className={style.verificationCode}
                        onClick={() => setCode(verificationCode)}
                    >
                        {verificationCode}
                    </div>
                )}
                <div className={style.container}>
                    <div className={style.content}>
                        <div>
                            <Headline3 className={style.title}>
                                Введите код из&nbsp;письма
                            </Headline3>
                        </div>
                        <div className={style.code}>
                            <input
                                ref={inputRef}
                                className={cn(style.codeInput, {
                                    [style.error]: status === "error",
                                    [style.success]: status === "verified",
                                })}
                                type="text"
                                value={code}
                                onChange={change}
                                onPaste={paste}
                                onInput={inputHandler}
                            />
                        </div>
                        {status === "sending" || status === "verifying" ? (
                            <Spinner />
                        ) : (
                            <Text isSecondary className={style.text}>
                                {status === "error" ? (
                                    <span className={style.error}>{error}</span>
                                ) : (
                                    <>
                                        Мы&nbsp;прислали письмо с&nbsp;кодом
                                        <br />
                                        на {email}.
                                    </>
                                )}
                                <br />
                                {resendTimeout >= new Date() ? (
                                    <>
                                        Прислать еще раз через{" "}
                                        {format(new Date(countdown), "mm:ss")}
                                    </>
                                ) : (
                                    <button className={style.resend} onClick={() => void send()}>
                                        Прислать еще раз
                                    </button>
                                )}
                            </Text>
                        )}
                    </div>
                </div>
                <hr className={style.hr} />
                <Button onClick={cancel}>Продолжить без промокода</Button>
            </Modal.Content>
        </Modal>
    )
}
